import styles from "./BlockExtraCard.module.css"
import { useRef } from 'react';
import CartButtonSecondary from "../../Button/CartButton/CartButtonSecondary";
import CartAmountSecondary from "../../Button/CartButton/CartAmountSecondary";
import { StorageItemCardProps } from "../../../types/storageItem.types";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import { useOrderContext } from "../../../context/OrderContext";
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { czk_to_eur, formatCurrency } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { useState } from 'react';
import { normalizeDate, denormalizeDate } from "../../../utils/calendar";
import { getPrimaryProductImage } from "../../../services/productImageService";
import { usePopup } from "../../../context/PopupContext";
import Tooltip from "../../Tooltip/Tooltip";
import { defaultOrderTime } from "../../../constants/times";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import useCartAmountSync from "../../../hooks/useCartAmountSync";
import useCardClickGuard from "../../../hooks/useCardClickGuard";
import { getAdvanceDays, getDeliveryWindow } from "../../../utils/products";
import { getQuantityTooltip } from "../../../utils/quantity";
/**
* Block card for storage items
*
* @param props StorageItemCardProps
* @param {StorageItem} props.product - Product
* @returns TSX.Element
*/
const BlockExtraCard = ({ product }: StorageItemCardProps) => {
    const {isPublicView} = usePublicCatalog();
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t, i18n } = useTranslation(); // Translation
    const { openDetail } = useDetailContext(); // Detail context
    const { orderData, orderType } = useOrderContext(); // Order data
    const { openTimeModal } = useTimeModalActions(); // Time modal
    const { selectedCurrency } = usePreferences(); // Current selected currency

    const dateKey = denormalizeDate(orderData.selectedDate);
    const dateISO = normalizeDate(orderData.selectedDate);
    const { cartItem: firstCartInstance, cartAmount, handleCart } = useCartAmountSync({
        cartData,
        cartLoaded,
        reloadCart,
        itemKey: {
            id: product.id,
            category: "storage",
            dateKey,
            dateISO,
        },
    });
    const { open: openPopup, close: closePopup, setPopupTarget } = usePopup();
    const buttonRef = useRef<HTMLDivElement>(null)

    // Handle opening detail
    const handleOpen = useCardClickGuard([buttonRef], () => {
        openDetail && openDetail({id: product.id, type: "storage" }); 
    });

    // Make sure price is always in correct currency
    const no_dph_price = product.pricing?.price_without_tax != null ? czk_to_eur(product.pricing.price_without_tax, czk_eur_rate, selectedCurrency).toFixed(2) : "N/A"
    const dph_price = product.pricing?.price_with_tax != null ? czk_to_eur(product.pricing.price_with_tax, czk_eur_rate, selectedCurrency).toFixed(2) : "N/A"

    // Order in advance
    const advance = getAdvanceDays(product);

    // Sample time limit
    const { min: minTime, max: maxTime } = getDeliveryWindow(product);

    const minOrder = product.min_order ?? 1;
    const granularity = product.granularity ?? 1;
    const tooltip = getQuantityTooltip({
        cartAmount,
        minOrder,
        granularity,
        language: i18n.language,
        itemType: "service",
    });

    const [showTooltip, setShowTooltip] = useState(false);

    return(
        <>
            <div className={styles.ProductBlock} key={product.id} onClick={handleOpen}>
                <div className={styles.ProductIImageCenter}>
                    <div className={styles.ProductImage} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}></div>
                </div>
                <div className={styles.ProductContent}>
                    <div className={styles.ProductDescription}>
                        <p>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                    </div>
                    <div className={styles.ProductInfo}>
                        <div className={styles.ProductPrice}>
                            {product.pricing?.price_without_tax != null && (
                                <p className={styles.NoDph}>{formatCurrency(no_dph_price, selectedCurrency)}/<Trans i18nKey="products.no_dph" components={{ 1: <span/> }} /></p>
                            )}
                            {product.pricing?.price_with_tax != null && (
                                <p className={styles.WithDph}>{formatCurrency(dph_price, selectedCurrency)} {t("products.dph")}</p>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.CartButtons} ref={buttonRef}>
                    {(cartAmount>0 && !isPublicView) ? (
                        < div className = {styles.Amount} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} >
                        <CartAmountSecondary amountInCart={cartAmount} 
                            /*disabledDecrease={cartAmount <= (product?.min_order === null ? 1 : product.min_order)}*/
                            disabledIncrease={ cartAmount >= ((product.max_order === null || product.max_order === 0) ? 100 : product.max_order)}
                            onIncrease={() => handleCart(cartAmount+ (1*(product.granularity??1)))}
                            onDecrease={()=>{ cartAmount > (product?.min_order ?? 1) ? handleCart(cartAmount-(1*(product.granularity??1))) :  handleCart(0)}}
                            className={styles.CartAmount} 
                            disabled={ isPublicView}
                            />
                            {showTooltip && <Tooltip desc={tooltip} className={styles.Tooltips} targetRef={buttonRef}>{tooltip}</Tooltip>}
                        </div>
                    ) : (
                        <CartButtonSecondary text={t("buttons.demand")} 
                        disabled={ isPublicView}
                        onClick={()=>{
                            if(orderType !== "demand"){

                                setPopupTarget(
                                        {
                                            title: t("demand_popup.title"),
                                            content: t("demand_popup.content"),
                                            submitBtnText: t("demand_popup.continue"),
                                            backBtnText: t("demand_popup.back"),
                                            onClose: (()=>{closePopup()}),
                                            onSubmit: (() => {
                                                openTimeModal({
                                                    type: "single",
                                                    date: orderData.selectedDate,
                                                    time: defaultOrderTime,
                                                    productId: product.id,
                                                    prodType: "storage",
                                                    static: true,
                                                    amount: Math.max(product.min_order ?? 1, product.granularity) ?? 1,
                                                    restrictions: { min: minTime, max: maxTime },
                                                    advance: advance,
                                                });
                                                closePopup();
                                            })
                                        }
                                    )
                                openPopup()
                            }else{
                                openTimeModal({
                                    type: "single",
                                    date: orderData.selectedDate,
                                    time: defaultOrderTime,
                                    productId: product.id,
                                    prodType: "storage",
                                    static: true,
                                    amount: Math.max(product.min_order ?? 1, product.granularity) ?? 1,
                                    restrictions: { min: minTime, max: maxTime },
                                    advance: advance,
                                });
                            }
                        }} 
                        className={styles.CartButton}/>
                    )}
                </div>
            </div>
        </>
    )
}
export default BlockExtraCard
